import {
  download,
  EllipsedText,
  FlexColumn,
  FlexRow,
  getFileNameFromPath,
  isValidImage,
  LabeledInput,
  MaterialIcon,
  useModal,
} from '@gorila-shared-ui/components';
import { Block } from 'baseui/block';
import { Card } from 'baseui/card';
import { Checkbox } from 'baseui/checkbox';
import { DatePicker, TimePicker } from 'baseui/datepicker';
import { StyledDivider } from 'baseui/divider';
import { FormControl } from 'baseui/form-control';
import { Input } from 'baseui/input';
import { Textarea } from 'baseui/textarea';
import { LabelSmall, ParagraphSmall, ParagraphXSmall } from 'baseui/typography';
import { useEffect, useState } from 'react';
import { useFormFieldConfig } from '../../hooks/useFormFieldConfig';
import { useStyles } from '../../hooks/useStyles';
import { getJobActivitiesFiles } from '../../services/jobsService';
import { FormField } from '../../types/forms';
import { ImagePreview } from '../shared/ImagePreview';
import EnhancedLabeledSelect from '../ui/EnhancedLabeledSelect';

type Props = {
  jobId: string;
  field: FormField;
};

export function FormFieldResume({ field, jobId }: Readonly<Props>) {
  const { theme, css } = useStyles();
  const { answers, options } = field;
  const fieldConfig = useFormFieldConfig(field.fieldType);
  const [imageUrl, setImageUrl] = useState<string>();
  const { showModal, openModal, closeModal } = useModal();

  const fieldLabel = `${field.name} ${field.required ? '*' : ''}`;
  const isImage = field.fieldType === 'image' || field.fieldType === 'canvas';

  const getImageUrl = async () => {
    if (answers && answers[0]) {
      if (!isValidImage(answers[0])) return;
      const { url } = await getJobActivitiesFiles(jobId, answers[0]);
      setImageUrl(url);
    }
  };

  const downloadFile = async () => {
    if (answers && answers[0]) {
      const filename = getFileNameFromPath(answers[0]);
      if (isImage) {
        if (imageUrl) {
          download(imageUrl, filename);
          return;
        }
      }
      const { url } = await getJobActivitiesFiles(jobId, answers[0]);
      if (url) {
        download(url, filename);
      }
    }
  };

  useEffect(() => {
    if (isImage) {
      getImageUrl();
      return;
    }
    setImageUrl(undefined);
  }, [isImage]);

  const helpText = (() => {
    return field.help_text ? (
      <ParagraphXSmall
        marginTop={theme.sizing.scale0}
        marginBottom={theme.sizing.scale300}
        color={theme.colors.contentTertiary}
      >
        {field.help_text}
      </ParagraphXSmall>
    ) : null;
  })();

  return (
    <Card
      overrides={{
        Root: {
          style: {
            borderLeftWidth: '1px',
            borderRightWidth: '1px',
            borderTopWidth: '1px',
            borderBottomWidth: '1px',
          },
        },
        Contents: {
          style: {
            marginTop: 0,
            marginRight: 0,
            marginBottom: 0,
            marginLeft: 0,
          },
        },
        Body: {
          style: {
            marginBottom: 0,
          },
        },
      }}
    >
      <FlexRow
        classNames={css({
          padding: `${theme.sizing.scale200} ${theme.sizing.scale400}`,
          alignItems: 'center',
          justifyContent: 'space-between',
        })}
      >
        <LabelSmall>{fieldLabel}</LabelSmall>
      </FlexRow>
      <StyledDivider
        $size="cell"
        style={{ marginTop: 0, marginBottom: 0 }}
      />
      <FlexColumn classNames={css({ padding: theme.sizing.scale400 })}>
        {!answers?.length && (
          <FlexRow classNames={css({ flex: 1, marginTop: theme.sizing.scale200 })}>
            <LabeledInput
              value={'Esta pregunta no ha sido respondida.'}
              readonly
            />
          </FlexRow>
        )}
        {!!answers?.length && (
          <FlexColumn gap={theme.sizing.scale200}>
            {fieldConfig.renderType === 'input' && (
              <FormControl
                caption={field.help_text}
                overrides={{
                  LabelContainer: {
                    style: {
                      marginTop: 0,
                      marginBottom: 0,
                    },
                  },
                  ControlContainer: {
                    style: { marginBottom: 0 },
                  },
                }}
              >
                <Input
                  endEnhancer={field.sufix}
                  name={field.name}
                  placeholder={field.name}
                  required={field.required}
                  size="compact"
                  startEnhancer={field.prefix}
                  type={fieldConfig.inputType}
                  value={answers[0] ?? '-'}
                  readOnly
                />
              </FormControl>
            )}
            {fieldConfig.renderType === 'textarea' && (
              <FormControl
                caption={field.help_text}
                overrides={{
                  LabelContainer: {
                    style: {
                      marginTop: 0,
                      marginBottom: 0,
                    },
                  },
                  ControlContainer: {
                    style: { marginBottom: 0 },
                  },
                }}
              >
                <Textarea
                  name={field.name}
                  placeholder={field.name}
                  required={field.required}
                  size="compact"
                  value={answers[0] ?? '-'}
                  readOnly
                />
              </FormControl>
            )}
            {fieldConfig.renderType === 'checkbox' && (
              <FormControl
                caption={field.help_text}
                overrides={{
                  LabelContainer: {
                    style: {
                      marginTop: 0,
                      marginBottom: 0,
                    },
                  },
                  ControlContainer: {
                    style: { marginBottom: 0 },
                  },
                }}
              >
                <Checkbox
                  checked={answers[0] === 'true'}
                  disabled
                >
                  {field.name}
                </Checkbox>
              </FormControl>
            )}
            {fieldConfig.renderType === 'canvas' && (
              <FlexColumn
                gap={theme.sizing.scale200}
                classNames={css({
                  width: '100%',
                  cursor: 'default',
                })}
              >
                <Block
                  onClick={isImage && imageUrl ? openModal : downloadFile}
                  className={css({
                    cursor: 'pointer',
                    paddingTop: theme.sizing.scale300,
                    paddingBottom: theme.sizing.scale300,
                    paddingLeft: theme.sizing.scale550,
                    paddingRight: theme.sizing.scale550,
                    borderRadius: theme.borders.inputBorderRadius,
                    backgroundColor: theme.colors.backgroundTertiary,
                  })}
                >
                  <FlexRow
                    gap={theme.sizing.scale300}
                    classNames={css({
                      width: '100%',
                    })}
                  >
                    <div className={css({ lineHeight: 0 })}>
                      {!isImage && (
                        <MaterialIcon
                          name={(() => {
                            if (field.fieldType === 'file') return 'file';
                            if (field.fieldType === 'signature') return 'signature';
                            return '';
                          })()}
                          size="mini"
                          color={theme.colors.contentTertiary}
                        />
                      )}
                      {isImage && (
                        <>
                          {!!imageUrl && (
                            <img
                              src={imageUrl}
                              alt=""
                              className={css({
                                height: theme.sizing.scale700,
                                objectFit: 'cover',
                              })}
                            />
                          )}
                          {!imageUrl && (
                            <MaterialIcon
                              name={(() => {
                                if (field.fieldType === 'image') return 'image';
                                if (field.fieldType === 'canvas') return 'stylus_note';
                                return '';
                              })()}
                              size="mini"
                              color={theme.colors.contentTertiary}
                            />
                          )}
                        </>
                      )}
                    </div>
                    <ParagraphSmall margin={0}>
                      <EllipsedText
                        breakAll
                        inline
                      >
                        {getFileNameFromPath(answers[0]) ?? '-'}
                      </EllipsedText>
                    </ParagraphSmall>
                  </FlexRow>
                </Block>
                {helpText}
              </FlexColumn>
            )}
            {fieldConfig.renderType === 'date' && (
              <FlexColumn gap={theme.sizing.scale200}>
                <FlexRow gap={theme.sizing.scale200}>
                  <div className={css({ width: '120px' })}>
                    <DatePicker
                      size="compact"
                      value={new Date(+answers[0])}
                      onChange={undefined}
                      mask="9999/99/99"
                      disabled
                    />
                  </div>
                </FlexRow>
                {helpText}
              </FlexColumn>
            )}
            {fieldConfig.renderType === 'time' && (
              <FlexColumn gap={theme.sizing.scale200}>
                <div className={css({ width: '120px' })}>
                  <TimePicker
                    size="compact"
                    value={new Date(+answers[0])}
                    onChange={undefined}
                    disabled
                  />
                </div>
                {helpText}
              </FlexColumn>
            )}
            {fieldConfig.renderType === 'datetime' && (
              <FlexColumn gap={theme.sizing.scale200}>
                <FlexRow gap={theme.sizing.scale200}>
                  <div className={css({ width: '120px' })}>
                    <DatePicker
                      size="compact"
                      value={new Date(+answers[0])}
                      onChange={undefined}
                      mask="9999/99/99"
                      disabled
                    />
                  </div>
                  <div className={css({ width: '120px' })}>
                    <TimePicker
                      size="compact"
                      value={new Date(+answers[0])}
                      onChange={undefined}
                      disabled
                    />
                  </div>
                </FlexRow>
                {helpText}
              </FlexColumn>
            )}
            {fieldConfig.renderType === 'select' && (
              <>
                <EnhancedLabeledSelect
                  value={answers.map((answer) => ({ id: options?.find((option) => option.value === answer)?.value }))}
                  placeholder="Selecciona..."
                  required={field.required}
                  options={field.options?.map((option) => ({ id: option.value, label: option.text }))}
                  onChange={() => {}}
                  disabled
                />
                {helpText}
              </>
            )}
            {fieldConfig.renderType === 'radio' && (
              <>
                <EnhancedLabeledSelect
                  placeholder="Selecciona..."
                  value={[{ id: options?.find((option) => option.text === answers[0])?.value }]}
                  required={field.required}
                  options={field.options?.map((option) => ({ id: option.value, label: option.text }))}
                  onChange={() => {}}
                  disabled
                />
                {helpText}
              </>
            )}
          </FlexColumn>
        )}
      </FlexColumn>
      {isImage && imageUrl && (
        <ImagePreview
          isOpen={showModal}
          onClose={closeModal}
          imgUrl={imageUrl}
          onDownload={downloadFile}
        />
      )}
    </Card>
  );
}
